import { Mail, Phone, MapPin, Linkedin, Globe, Github, Calendar, Award } from 'lucide-react'
import { getColorClasses } from '@/lib/templateColors'

interface ResumeTemplateProps {
  data: any
  language?: string
  labels: any
  color?: string
}


export default function CombinedTemplate({ data, labels, color = 'blue' }: ResumeTemplateProps) {
  const colors = getColorClasses(color)
  const { personalInfo, summary, experience, education, skills, projects, certifications } = data

  return (

      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden">
        <div id="resume-preview" className="bg-white" style={{ width: '8.5in', minHeight: '11in', margin: '0 auto' }}>
          {/* Header */}
          <div className={`${colors.headerBg} ${colors.headerText} p-8`}>
            <div className={`border-l-4 ${colors.accentBorder} pl-4`}>
              <h1 className="text-4xl font-bold mb-2">{personalInfo.fullName || 'YOUR NAME'}</h1>
              <div className={`${colors.accentText} text-lg`}>{personalInfo.email}</div>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              {personalInfo.phone && <span>{personalInfo.phone}</span>}
              {personalInfo.address && <span>{personalInfo.address}</span>}
              {personalInfo.website && <span>{personalInfo.website}</span>}
            </div>
          </div>

          <div className="grid grid-cols-3">
            {/* Left Sidebar */}
            <div className={`col-span-1 bg-gradient-to-b ${colors.badgeBg} p-6`}>
              {/* Skills */}
              {skills && skills.length > 0 && (
                <div className="mb-6">
                  <h2 className={`text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>
                    {labels.skills}
                  </h2>
                  {skills.map((skillGroup: any, idx: number) => (
                    <div key={idx} className="mb-4">
                      <div className={`text-xs font-bold ${colors.accentText} mb-2`}>{skillGroup.category}</div>
                      <div className="flex flex-wrap gap-1">
                        {skillGroup.skills?.map((skill: string, i: number) => (
                          <span key={i} className="text-xs bg-white text-gray-700 px-2 py-1 rounded shadow-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Education */}
              {education && education.length > 0 && (
                <div>
                  <h2 className={`text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>
                    {labels.education}
                  </h2>
                  {education.map((edu: any) => (
                    <div key={edu.id} className="mb-3">
                      <div className="text-sm font-bold text-gray-900">{edu.degree}</div>
                      <div className={`text-xs ${colors.accentText}`}>{edu.institution}</div>
                      <div className="text-xs text-gray-600">{edu.graduationDate}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Main Content */}
            <div className="col-span-2 p-6">
              {/* Summary */}
              {summary && (
                <div className="mb-6">
                  <h2 className={`text-lg font-bold text-gray-900 mb-3 pb-2 border-b-2 ${colors.sectionBorder}`}>
                    {labels.professionalSummary}
                  </h2>
                  <p className="text-gray-700 leading-relaxed text-sm">{summary}</p>
                </div>
              )}

              {/* Experience */}
              {experience && experience.length > 0 && (
                <div>
                  <h2 className={`text-lg font-bold text-gray-900 mb-4 pb-2 border-b-2 ${colors.sectionBorder}`}>
                    {labels.workExperience}
                  </h2>
                  {experience.map((exp: any) => (
                    <div key={exp.id} className="mb-4">
                      <div className="flex justify-between items-baseline mb-1">
                        <h3 className="text-base font-bold text-gray-900">{exp.jobTitle}</h3>
                        <span className="text-xs text-gray-600">{exp.startDate} - {exp.endDate || labels.present}</span>
                      </div>
                      <div className={`text-sm font-semibold ${colors.accentText} mb-2`}>{exp.company}</div>
                      <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{exp.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  )
}
